// external
import React, { FC, useEffect, useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

// internal
import './equipment-viewer.page.css';
import logo from '../../assets/CoeLab-logo-full.png';
// import appIcon from '../../assets/logo512.png';
import useService from '../../hooks/service.hook';
import EquipmentService from '../../services/equipments.service';
import COESkeletonComponent from '../../components/general/coe-skeleton/coe-skeleton.component';

/** [field value key, label] */
const SPEC_FIELDS: [string, string][] = [
  ['Grind', 'Grind'],
  ['Ski Length in cm', 'Ski Length'],
  ['Camber Height', 'Camber Height'],
  ['Base Material', 'Base Material'],
  ['MF 0.2', 'MF 0.2'],
  ['Serial #', 'Serial #'],
];

function extractFieldEntry(raw: unknown): { value: string; unit: string } {
  if (raw != null && typeof raw === 'object' && 'value' in raw) {
    const obj = raw as { value?: unknown; measurement_unit?: string | null };
    return {
      value: obj.value != null ? String(obj.value) : '',
      unit: obj.measurement_unit ?? '',
    };
  }
  return { value: raw != null ? String(raw) : '', unit: '' };
}

const EquipmentViewerPage: FC = () => {
  const equipmentService = useService(EquipmentService);
  const { id } = useParams();
  const [equipment, setEquipment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (id == null || id === '') {
      setLoading(false);
      setError(true);
      return;
    }
    setError(false);
    setErrorCode(null);
    equipmentService
      .getPublicEquipment(id)
      .then((res) => {
        setEquipment(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch equipment:', err);
        setLoading(false);
        setError(true);
        setErrorCode(err.response?.status ?? 500);
      });
  }, [id]);

  return (
    <Box className="equipment-viewer-container">
      <Box className="equipment-viewer-header">
        <img src={logo} className="equipment-viewer-logo" alt="CoeLab" />
        <Divider className="header-divider" />
      </Box>

      {loading && (
        <Card className="equipment-card">
          <CardContent>
            <COESkeletonComponent />
            <COESkeletonComponent />
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="equipment-card">
          <CardContent>
            <Typography className="equipment-title">
              {errorCode === 404 ? 'Equipment not found' : 'Unknown Error'}
            </Typography>
            <Typography className="equipment-id">{errorCode}</Typography>
          </CardContent>
        </Card>
      )}

      {!loading && !error && equipment != null && (
        <>
          <Button
            className={(() => {
              const ref = extractFieldEntry(equipment.standard_field_values?.['Snow Designation']).value.toLowerCase();
              if (ref.includes('zero')) return 'designation-button zero-button';
              if (ref.includes('warm')) return 'designation-button warm-button';
              if (ref.includes('cold')) return 'designation-button cold-button';
              return 'designation-button';
            })()}
            variant="contained"
          >
            {extractFieldEntry(equipment.standard_field_values?.['Snow Designation']).value.toUpperCase() || 'N/A'}
          </Button>

          <Card className="equipment-card">
            <CardContent>
              <Typography className="equipment-title">{equipment.name}</Typography>
              <Typography className="equipment-id">
                #{extractFieldEntry(equipment.standard_field_values?.['Serial #']).value.slice(-3) || 'N/A'}
              </Typography>

              <Box className="manufacturer-year">
                <Typography className="manufacturer-year-text">{equipment.brand}</Typography>
                <Typography className="separator">|</Typography>
                <Typography className="manufacturer-year-text">{equipment.year}</Typography>
              </Box>

              <Box className="equipment-type-row">
                <div className="equipment-type">
                  <Typography className="equipment-type-bold">{equipment.equipment_type.name}</Typography>
                  <Typography className="separator">-</Typography>
                  <Typography className="equipment-type-bold">{equipment.equipment_subtype.name}</Typography>
                </div>
                <Button
                  className={(() => {
                    const ref = extractFieldEntry(
                      equipment.standard_field_values?.['Snow Designation']
                    ).value.toLowerCase();
                    if (ref.includes('zero')) return 'designation-tag zero-tag';
                    if (ref.includes('warm')) return 'designation-tag warm-tag';
                    if (ref.includes('cold')) return 'designation-tag cold-tag';
                    return 'designation-tag';
                  })()}
                  variant="contained"
                  size="small"
                >
                  {extractFieldEntry(equipment.standard_field_values?.['Snow Designation']).value.toUpperCase() ||
                    'N/A'}
                </Button>
              </Box>

              <Divider className="equipment-divider" />

              <Grid container className="spec-grid" spacing={2}>
                {SPEC_FIELDS.map(([key, label]) => {
                  const entry = extractFieldEntry(equipment.standard_field_values?.[key]);
                  const displayValue = entry.value
                    ? entry.unit
                      ? `${entry.value} ${entry.unit}`
                      : entry.value
                    : 'N/A';
                  return (
                    <Grid item xs={4} key={key}>
                      <Box className="spec-item">
                        <Typography className="spec-value">{displayValue}</Typography>
                        <Typography className="spec-label">{label}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider className="equipment-divider" />

              <Box className="dates-container">
                <Typography className="date-text">
                  Created: {equipment.created_at ? new Date(equipment.created_at).toLocaleDateString() : '-'}
                </Typography>
                <Typography className="date-text">
                  Updated: {equipment.updated_at ? new Date(equipment.updated_at).toLocaleDateString() : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Typography className="user-name">
            {equipment.owner.firstname} {equipment.owner.lastname}
          </Typography>

          <Box className="action-buttons">
            <Button
              className="sign-in-button"
              variant="contained"
              onClick={() => {
                const redirectPath = '/equipment';
                navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
              }}
            >
              Sign in
            </Button>
            {/* <Button className="open-app-button" variant="outlined">
              <img src={appIcon} className="app-icon" alt="CoeLab" />
              Open App
            </Button> */}
          </Box>

          <Box className="download-buttons">
            <Button
              className="app-store-button"
              onClick={() => window.open('https://apps.apple.com/us/app/coelab/id6502800260', '_blank')}
            >
              <img src="/app_store_icon.svg" className="app-store-badge" alt="Download on the App Store" />
            </Button>
            <Button
              className="google-play-button"
              onClick={() =>
                window.open(
                  'https://play.google.com/store/apps/details?id=com.coelab.apps.android&pcampaignid=web_share',
                  '_blank'
                )
              }
            >
              <img src="/google_play_icon.svg" className="google-play-badge" alt="Get it on Google Play" />
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EquipmentViewerPage;
